// uploadHandler.js?v=6.0.43
import db from './db.js';
import events from './event.js';
import Storage from './storage.js';
import Logger from './logger.js';
import imageHelper from './imageHelper.js';
import CoverDialog from './coverDialog.js';
import imageTokens from './imageTokens.js';

class UploadHandler {
    isUploading = false;

    constructor() {
        // OLD trigger will wait for load to be complete
        // events.add('newImageLocalSaved', () => this.startQueue());

        // NEW not waiting for load to be complete
        events.add('newImageLocalSaved', () => {
            this.startQueue().catch(err => Logger.error('Unhandled upload queue error', err));
        });
    }

    async startQueue() {
        if (this.isUploading) return;
        this.isUploading = true;

        try {
            const images = await db.getAllImages(); // Anpassa efter din db-implementation
            const pendingImages = images.filter(img => img.uploadStatus !== 'uploaded');

            if (pendingImages.length === 0) {
                // events.trigger('uploadQueueEmpty');
                this.isUploading = false;
                return;
            }

            for (const img of pendingImages) {
                const result = await this.uploadImage(img);
                if (result.success) {
                    const originalStatus = img.uploadStatus;

                    const imageResolution = img.file ? await imageHelper.getImageResolution(result.url) : null;
                    if (imageResolution && imageResolution.width === 0 && imageResolution.height === 0) {
                        Logger.warn('Failed to get image resolution after upload for image ID:', img.id);
                    }

                    // Uppdatera databasen med den nya statusen och URL:en
                    await db.updateImageData(img.id, { uploadStatus: 'uploaded', serverUrl: result.url, imageResolution });
                    Logger.log('Image uploaded to server.'); //, img.id);
                    Storage.save('lastUploadedImageURL', result.url);

                    if (img.id == Storage.get('activeImageID')) {
                        const orientation = imageHelper.getImageOrientation(imageResolution);
                        Storage.save('orientation', orientation);
                        // Storage.remove('cropBox');
                        Logger.debug('Active image uploaded, orientation saved:', orientation);
                    }
                    events.trigger('imageUploadStatusChanged', {
                        id: img.id,
                        newStatus: 'uploaded',
                        serverUrl: result.url,
                        originalStatus: originalStatus
                    });

                    events.trigger('updateNavigation');

                    // Persist the session as soon as the upload finishes, so the
                    // image index (now carrying serverUrl + 'uploaded' status)
                    // survives a reload. Without this, commitSession() only ran
                    // on a gotoProduct navigation and the uploaded-image state
                    // could be lost to a reload before that ever happened.
                    Storage.commitSession();
                    // events.trigger('imageUploadSuccess', { id: img.id, url: result.url });
                } else {
                    await db.updateImageData(img.id, { uploadStatus: 'failed' });
                    events.trigger('imageUploadFailed', { id: img.id, error: result.error });
                    // events.trigger('imageUploadError', { id: img.id, error: result.error });
                }
            }

            // events.trigger('uploadQueueComplete');
        } catch (err) {
            Logger.error('Upload queue error:', err);
            // events.trigger('uploadQueueError', err);
        } finally {
            this.isUploading = false;
        }
    }

    async uploadImage(imageData) {
        if (!(imageData.file instanceof File)) {
            return { success: false, error: 'Invalid file type' };
        }

        // Inställningar för chunking
        const CHUNK_SIZE = 0.5 * 1024 * 1024; // 0.5 MB per bit
        const totalSize = imageData.file.size;
        const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);

        let currentChunk = 0;
        let start = 0;

        // Unikt filnamn/ID att skicka med så servern vet vilken fil bitarna tillhör
        const fileId = imageData.id;

        try {
            let serverResponse = null;

            while (start < totalSize) {
                const end = Math.min(start + CHUNK_SIZE, totalSize);
                const chunk = imageData.file.slice(start, end);

                const formData = new FormData();
                formData.append('file', chunk); // Filen heter fortfarande 'file' i PHP
                formData.append('id', fileId);
                formData.append('chunk_index', currentChunk);
                formData.append('total_chunks', totalChunks);
                formData.append('original_name', imageData.file.name); // Bra att ha för extension
                formData.append('token', imageTokens.get(fileId));

                // Skicka biten
                const res = await fetch('/wp-json/mscp/v1/upload', {
                    method: 'POST',
                    body: formData,
                });

                if (!res.ok) {
                    const json = await res.json().catch(() => ({}));
                    throw new Error(json.message || `HTTP ${res.status}`);
                }

                serverResponse = await res.json();
                if (serverResponse && serverResponse.token) imageTokens.set(fileId, serverResponse.token);

                // Om något gick fel i backend (t.ex. validering på sista biten)
                if (!serverResponse.success) {
                    throw new Error(serverResponse.message || 'Unknown server error');
                }

                // Uppdatera progress
                currentChunk++;
                start += CHUNK_SIZE;

                const progressPercent = Math.round((currentChunk / totalChunks) * 100);

                // Trigga ett event för UI så du kan visa en bar
                events.trigger('uploadProgress', {
                    id: fileId,
                    percent: progressPercent
                });

                Logger.debug(`Uploaded chunk ${currentChunk}/${totalChunks} for ${fileId}`);
            }

            // När loopen är klar returnerar vi svaret från sista biten (som innehåller URL)
            return { success: true, url: serverResponse.url };

        } catch (error) {
            CoverDialog.show({
                type: 'uploadFailed',
                data: { errorMessage: error.message }
            });
            console.error('Upload failed for image ID:', imageData.id, 'Error:', error);
            // Logger.error('Upload failed:', error);
            return { success: false, error: error.message };
        }
    }
}

const uploadHandler = new UploadHandler();
export default uploadHandler;